import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminSpeakers, createSpeaker, updateSpeaker, deleteSpeaker } from '../../api/content';
import type { Speaker } from '../../api/content';

const BACKEND_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

function SpeakerModal({
  speaker,
  onClose,
}: {
  speaker: Speaker | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(speaker?.nome ?? '');
  const [cognome, setCognome] = useState(speaker?.cognome ?? '');
  const [bio, setBio] = useState(speaker?.bio ?? '');
  const [azienda, setAzienda] = useState(speaker?.azienda ?? '');
  const [linkedin, setLinkedin] = useState(speaker?.linkedin_url ?? '');
  const [email, setEmail] = useState(speaker?.email ?? '');
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nome.trim() || !cognome.trim()) { setError('Nome e cognome sono obbligatori'); return; }
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('nome', nome.trim());
      fd.append('cognome', cognome.trim());
      fd.append('bio', bio.trim());
      fd.append('azienda', azienda.trim());
      fd.append('linkedin_url', linkedin.trim());
      fd.append('email', email.trim());
      if (photo) fd.append('photo', photo);
      if (speaker) {
        await updateSpeaker(speaker.id, fd);
      } else {
        await createSpeaker(fd);
      }
      await qc.invalidateQueries({ queryKey: ['admin-speakers'] });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{speaker ? 'Modifica relatore' : 'Nuovo relatore'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--gray-400)' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {error && <div className="alert alert-danger">{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input className="form-control" value={nome} onChange={e => setNome(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Cognome *</label>
              <input className="form-control" value={cognome} onChange={e => setCognome(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Azienda / Ente</label>
            <input className="form-control" value={azienda} onChange={e => setAzienda(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea className="form-control" rows={3} value={bio} onChange={e => setBio(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="relatore@esempio.it" />
          </div>
          <div className="form-group">
            <label className="form-label">LinkedIn URL</label>
            <input className="form-control" type="url" value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..." />
          </div>
          <div className="form-group">
            <label className="form-label">Foto</label>
            {speaker?.photo_url && !photo && (
              <img src={`${BACKEND_URL}${speaker.photo_url}`} alt="" style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', marginBottom: '0.5rem', display: 'block' }} />
            )}
            <input className="form-control" type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setPhoto(e.target.files?.[0] ?? null)} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvataggio...' : speaker ? 'Salva modifiche' : 'Crea relatore'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminSpeakersPage() {
  const qc = useQueryClient();
  const { data: speakers = [], isLoading } = useQuery({
    queryKey: ['admin-speakers'],
    queryFn: getAdminSpeakers,
  });

  const deleteMut = useMutation({
    mutationFn: deleteSpeaker,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-speakers'] }),
  });

  const [modal, setModal] = useState<'new' | Speaker | null>(null);

  function handleDelete(s: Speaker) {
    if (confirm(`Eliminare ${s.nome} ${s.cognome}?`)) deleteMut.mutate(s.id);
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Relatori</h1>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ Nuovo relatore</button>
      </div>

      {isLoading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : speakers.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p className="text-muted">Nessun relatore ancora. Aggiungine uno per iniziare.</p>
          <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => setModal('new')}>+ Aggiungi relatore</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {speakers.map(s => (
            <div key={s.id} className="card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                {s.photo_url ? (
                  <img src={`${BACKEND_URL}${s.photo_url}`} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>
                    👤
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{s.nome} {s.cognome}</div>
                  {s.azienda && <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.azienda}</div>}
                </div>
              </div>
              {s.bio && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.75rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.bio}</p>}
              {s.linkedin_url && (
                <a href={s.linkedin_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8125rem', color: 'var(--primary)' }}>LinkedIn →</a>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', borderTop: '1px solid var(--gray-100)', paddingTop: '0.75rem' }}>
                <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setModal(s)}>Modifica</button>
                <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={() => handleDelete(s)}>Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <SpeakerModal
          speaker={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
